import React, { useState, useEffect } from 'react'
import { HiPlus, HiPencil, HiTrash, HiSearch, HiFilter, HiX, HiUser } from 'react-icons/hi'
import { Button, Modal, TextInput, Label, Select, Badge, Spinner } from 'flowbite-react'
import axios from 'axios'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import UserProfileForm from '../components/UserProfileForm'

const UserAdmin = () => {
  const { token } = useAuth()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterRole, setFilterRole] = useState('all')
  const [showAddUser, setShowAddUser] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [isEditingUser, setIsEditingUser] = useState(false)
  const [saveTrigger, setSaveTrigger] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [itemsPerPage] = useState(10)
  const [validatingUsers, setValidatingUsers] = useState(new Set())

  const [newUser, setNewUser] = useState({
    full_name: '',
    email: '',
    full_address: '',
    house_number: '',
    road: '',
    address2: '',
    city: '',
    state: '',
    postcode: '',
    country_iso2: 'US',
    mobile_phone: '',
    latitude: null,
    longitude: null,
    timezone: '',
    is_active: true,
    is_superuser: false,
    account_validated: false
  })

  // API Base URL
  const API_BASE = 'https://api.cityseecityfix.com/api/v1'


  // Fetch users from API
  const fetchUsers = async () => {
    try {
      setLoading(true)
      
      const response = await axios.get(`${API_BASE}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setUsers(response.data || [])
      setTotalPages(Math.ceil((response.data?.length || 0) / itemsPerPage))
    } catch (error) {
      console.error('Error fetching users:', error)
      console.error('Error response:', error.response)
      toast.error('Failed to fetch users')
      // Fallback to sample data for development
      setUsers([
        {
          id: 1,
          name: 'John Smith',
          email: 'john.smith@city.gov',
          role: 'admin',
          city: 'Springfield',
          status: 'active',
          validated: true,
          lastLogin: '2024-01-15T10:30:00Z',
          createdAt: '2023-06-15T09:00:00Z'
        },
        {
          id: 2,
          name: 'Sarah Johnson',
          email: 'sarah.johnson@city.gov',
          role: 'moderator',
          city: 'Springfield',
          status: 'active',
          validated: false,
          lastLogin: '2024-01-14T16:45:00Z',
          createdAt: '2023-08-22T14:30:00Z'
        }
      ])
    } finally {
      setLoading(false)
    }
  }

  // Create new user
  const handleAddUser = async () => {
    try {
      const response = await axios.post(`${API_BASE}/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      })
      toast.success('User created successfully')
      setUsers([...users, response.data])
      setNewUser({ full_name: '', email: '', full_address: '', house_number: '', road: '', address2: '', city: '', state: '', postcode: '', country_iso2: 'US', mobile_phone: '', latitude: null, longitude: null, timezone: '', is_active: true, is_superuser: false, account_validated: false })
      setShowAddUser(false)
    } catch (error) {
      console.error('Error creating user:', error)
      toast.error('Failed to create user')
    }
  }

  // Update user
  const handleSaveEdit = async (formData) => {
    if (!editingUser) return
    
    try {
      console.log('Sending user data:', JSON.stringify(formData, null, 2))
      const response = await axios.put(`${API_BASE}/users/${editingUser.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      console.log('User update response:', response.data)
      setUsers(users.map(u => u.id === editingUser.id ? formData : u))
      setEditingUser(null)
      setIsEditingUser(false)
    } catch (error) {
      console.error('Error updating user:', error)
      console.error('Error response data:', error.response?.data)
      console.error('Error response status:', error.response?.status)
      
      // Show more specific error message
      if (error.response?.data?.detail) {
        console.log('Full error detail:', error.response.data.detail)
        if (Array.isArray(error.response.data.detail)) {
          const errorMessages = error.response.data.detail.map(err => 
            typeof err === 'string' ? err : `${err.loc?.join('.')}: ${err.msg}`
          ).join(', ')
          toast.error(`Validation Error: ${errorMessages}`)
        } else {
          toast.error(`Validation Error: ${error.response.data.detail}`)
        }
      } else if (error.response?.data?.message) {
        toast.error(`Error: ${error.response.data.message}`)
      } else {
        toast.error('Failed to update user')
      }
      
      throw error // Let UserProfileForm handle the error display
    }
  }

  // Delete user
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return
    
    try {
      await axios.delete(`${API_BASE}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setUsers(users.filter(u => u.id !== userId))
      toast.success('User deleted successfully')
    } catch (error) {
      console.error('Error deleting user:', error)
      toast.error('Failed to delete user')
    }
  }

  // Toggle user validation
  const handleToggleValidation = async (userId, currentValidated) => {
    
    // If user is already validated and trying to unvalidate, show info toast
    if (currentValidated) {
      toast('Users can only be activated at this time. Deactivation is not supported.', {
        duration: 7000,
        icon: 'ℹ️',
        style: {
          background: '#3B82F6',
          color: 'white',
        },
      })
      return
    }

    try {
      // Set loading state for this specific user
      setValidatingUsers(prev => new Set(prev).add(userId))
      
      await axios.post(`${API_BASE}/users/${userId}/validate`, {}, {
        headers: { 
          'Authorization': `Bearer ${token}` 
        }
      })
      
      // Update the user in the local state
      setUsers(users.map(u => 
        u.id === userId ? { ...u, account_validated: !currentValidated } : u
      ))
      
      toast.success(`User validated successfully`)
      
    } catch (error) {
      console.error('Error toggling validation:', error)
      toast.error(`Failed to validate user: ${error.response?.data?.detail || error.message}`)
    } finally {
      // Clear loading state for this user
      setValidatingUsers(prev => {
        const newSet = new Set(prev)
        newSet.delete(userId)
        return newSet
      })
    }
  }

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesRole = filterRole === 'all' || (filterRole === 'admin' ? user.is_superuser : !user.is_superuser)
    return matchesSearch && matchesRole
  })

  // Pagination
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  // Debug user data for duplicate IDs
  useEffect(() => {
    if (paginatedUsers.length > 0) {
      const userIds = paginatedUsers.map(u => u.id)
      const uniqueUserIds = [...new Set(userIds)]
      if (userIds.length !== uniqueUserIds.length) {
        console.warn('Duplicate user IDs found:', userIds.filter((id, index) => userIds.indexOf(id) !== index))
        console.warn('User data:', paginatedUsers)
      }
    }
  }, [paginatedUsers])

  useEffect(() => {
    fetchUsers()
    // Mount-only initial load. fetchUsers is recreated every render, so listing
    // it would refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Handle edit toggle
  const handleEditToggle = () => {
    setIsEditingUser(!isEditingUser)
  }

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditingUser(false)
    setEditingUser(null)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="xl" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              User Administration
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              Manage user accounts, roles, and permissions using the CitySeeCityFix API.
            </p>
          </div>
          <Button onClick={() => setShowAddUser(true)}>
            <HiPlus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="search">Search Users</Label>
            <div className="relative">
              <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <TextInput
                id="search"
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="role-filter">Filter by Role</Label>
            <Select
              id="role-filter"
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
            >
              <option value="all">All Roles</option>
              <option value="admin">Superuser</option>
              <option value="user">Regular User</option>
            </Select>
          </div>
          
          <div className="flex items-end">
            <Button color="gray" className="w-full">
              <HiFilter className="h-4 w-4 mr-2" />
              More Filters
            </Button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Users ({filteredUsers.length})
          </h3>
        </div>
        
        
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Powers
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  User
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Validated
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedUsers.map((user, index) => (
                <tr key={`user-${user.id}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex justify-center">
                      {user.is_superuser && (
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900 rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {user.full_name || 'No Name'}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {user.email}
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    <div>
                      <div>{user.city || 'No City'}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {user.state && user.postcode ? `${user.state}, ${user.postcode}` : 'No Location'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge color={user.is_active ? 'green' : 'red'}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={user.account_validated || false}
                        onChange={() => {
                          // This prevents the checkbox from actually changing state
                          // We handle the logic in onClick instead
                        }}
                        onClick={() => {
                          handleToggleValidation(user.id, user.account_validated)
                        }}
                        disabled={validatingUsers.has(user.id)}
                        className={`w-4 h-4 rounded focus:ring-2 focus:ring-offset-2 ${
                          user.account_validated 
                            ? 'text-gray-400 bg-gray-200 border-gray-300' 
                            : 'text-primary-600 bg-gray-100 border-gray-300 focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600'
                        }`}
                        title={user.account_validated ? 'Click to see validation status' : 'Click to validate'}
                      />
                      {validatingUsers.has(user.id) && (
                        <Spinner size="sm" />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setEditingUser(user)
                          setIsEditingUser(false)
                        }}
                        title="Edit"
                        className="px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white text-xs font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                      >
                        <HiPencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        title="Delete"
                        className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                      >
                        <HiTrash className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  color="gray"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  color="gray"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      <Modal show={showAddUser} onClose={() => setShowAddUser(false)} size="6xl">
        <Modal.Header>Add New User</Modal.Header>
        <Modal.Body>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="full_name">Full Name</Label>
              <TextInput
                id="full_name"
                type="text"
                value={newUser.full_name}
                onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                placeholder="Enter full name"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <TextInput
                id="email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                placeholder="Enter email address"
              />
            </div>
            <div>
              <Label htmlFor="full_address">Full Address</Label>
              <TextInput
                id="full_address"
                type="text"
                value={newUser.full_address || ''}
                onChange={(e) => setNewUser({...newUser, full_address: e.target.value})}
                placeholder="Enter full address"
              />
            </div>
            <div>
              <Label htmlFor="house_number">House Number</Label>
              <TextInput
                id="house_number"
                type="text"
                value={newUser.house_number || ''}
                onChange={(e) => setNewUser({...newUser, house_number: e.target.value})}
                placeholder="Enter house number"
              />
            </div>
            <div>
              <Label htmlFor="road">Road/Street</Label>
              <TextInput
                id="road"
                type="text"
                value={newUser.road || ''}
                onChange={(e) => setNewUser({...newUser, road: e.target.value})}
                placeholder="Enter road/street name"
              />
            </div>
            <div>
              <Label htmlFor="address2">Address Line 2</Label>
              <TextInput
                id="address2"
                type="text"
                value={newUser.address2 || ''}
                onChange={(e) => setNewUser({...newUser, address2: e.target.value})}
                placeholder="Apartment, suite, unit, etc."
              />
            </div>
            <div>
              <Label htmlFor="city">City</Label>
              <TextInput
                id="city"
                type="text"
                value={newUser.city}
                onChange={(e) => setNewUser({...newUser, city: e.target.value})}
                placeholder="Enter city"
              />
            </div>
            <div>
              <Label htmlFor="state">State</Label>
              <TextInput
                id="state"
                type="text"
                value={newUser.state}
                onChange={(e) => setNewUser({...newUser, state: e.target.value})}
                placeholder="Enter state"
              />
            </div>
            <div>
              <Label htmlFor="postcode">Postal Code</Label>
              <TextInput
                id="postcode"
                type="text"
                value={newUser.postcode}
                onChange={(e) => setNewUser({...newUser, postcode: e.target.value})}
                placeholder="Enter postal code"
              />
            </div>
            <div>
              <Label htmlFor="country_iso2">Country</Label>
              <Select
                id="country_iso2"
                value={newUser.country_iso2}
                onChange={(e) => setNewUser({...newUser, country_iso2: e.target.value})}
              >
                <option value="US">United States</option>
                <option value="CA">Canada</option>
                <option value="GB">United Kingdom</option>
                <option value="AU">Australia</option>
                <option value="DE">Germany</option>
                <option value="FR">France</option>
                <option value="JP">Japan</option>
                <option value="CN">China</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="mobile_phone">Mobile Phone</Label>
              <TextInput
                id="mobile_phone"
                type="tel"
                value={newUser.mobile_phone || ''}
                onChange={(e) => setNewUser({...newUser, mobile_phone: e.target.value})}
                placeholder="Enter mobile phone"
              />
            </div>
            <div>
              <Label htmlFor="latitude">Latitude</Label>
              <TextInput
                id="latitude"
                type="number"
                step="any"
                value={newUser.latitude || ''}
                onChange={(e) => setNewUser({...newUser, latitude: e.target.value ? parseFloat(e.target.value) : null})}
                placeholder="Enter latitude"
              />
            </div>
            <div>
              <Label htmlFor="longitude">Longitude</Label>
              <TextInput
                id="longitude"
                type="number"
                step="any"
                value={newUser.longitude || ''}
                onChange={(e) => setNewUser({...newUser, longitude: e.target.value ? parseFloat(e.target.value) : null})}
                placeholder="Enter longitude"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleAddUser}>Add User</Button>
          <Button color="gray" onClick={() => setShowAddUser(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit User Modal */}
      <Modal 
        show={!!editingUser} 
        onClose={() => {}} // Disable built-in close to use our custom close button
        size="4xl"
      >
        <div className="border-4 border-gray-300 dark:border-gray-600 rounded-lg shadow-lg">
          <div className="p-5 border-b border-gray-200 dark:border-gray-600">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleCancelEdit}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Close modal"
                >
                  <HiX className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </button>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900 rounded-lg">
                    <HiUser className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Edit User Profile
                  </h3>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Button 
                  color={isEditingUser ? "light" : "gray"}
                  onClick={handleCancelEdit}
                  disabled={!isEditingUser}
                  className={!isEditingUser ? 'opacity-50 cursor-not-allowed' : 'bg-blue-100 hover:bg-blue-200 text-blue-800'}
                >
                  Discard Changes
                </Button>
                <Button 
                  color={isEditingUser ? "success" : "gray"}
                  onClick={() => {
                    if (isEditingUser) {
                      setSaveTrigger(prev => prev + 1)
                    }
                  }}
                  disabled={!isEditingUser}
                  className={!isEditingUser ? 'opacity-50 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 text-white'}
                >
                  Save
                </Button>
                <Button 
                  color={isEditingUser ? "gray" : "blue"}
                  onClick={handleEditToggle}
                  disabled={isEditingUser}
                  className={isEditingUser ? 'opacity-50 cursor-not-allowed' : ''}
                >
                  Edit
                </Button>
              </div>
            </div>
          </div>
          <Modal.Body>
            <UserProfileForm
              user={editingUser}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
              isEditing={isEditingUser}
              onEditToggle={handleEditToggle}
              saveTrigger={saveTrigger}
              title=""
              subtitle=""
              showEditToggle={false}
              showSaveCancel={false}
              showHeader={false}
              showSystemInfo={true}
              showAccountStatus={true}
              showCoordinates={true}
            />
          </Modal.Body>
        </div>
      </Modal>
    </div>
  )
}

export default UserAdmin
